/**
 * HandoverReadOnlyDetail — presentational, strictly READ-ONLY view of a single
 * shift Handover Note for the Admin participant profile. Renders the authorised
 * fields returned by GET /handover/by-participant plus acknowledgement metadata.
 * Contains NO create / acknowledge / edit / delete controls and no API calls.
 * NOTE: GET /handover/by-participant does not enrich the author display name
 * (only staff_from_id is present); to avoid exposing a raw ID the author is not
 * shown here.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';

function fmt(dt) {
  if (!dt) return null;
  try { return new Date(dt).toLocaleString(); } catch { return String(dt); }
}

function Field({ label, value, testid, mono }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div data-testid={testid}>
      <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</h5>
      <p className={`text-sm whitespace-pre-wrap ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}

export function HandoverReadOnlyDetail({ handover }) {
  if (!handover) return null;
  const acks = handover.acknowledgements || [];

  return (
    <div className="space-y-4" data-testid="handover-readonly-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant={acks.length > 0 ? 'secondary' : 'outline'} data-testid="handover-ro-ack-badge">
          {acks.length > 0 ? `Acknowledged (${acks.length})` : 'Not acknowledged'}
        </Badge>
        {handover.created_at && <span className="text-xs text-muted-foreground" data-testid="handover-ro-created">{fmt(handover.created_at)}</span>}
      </div>

      <div className="grid gap-3 border-t pt-3">
        <Field label="Handover Summary" value={handover.handover_summary} testid="handover-ro-summary" />
        <Field label="Participant Wellbeing" value={handover.participant_wellbeing} testid="handover-ro-wellbeing" />
        <Field label="Medication Notes" value={handover.medication_notes} testid="handover-ro-medication" />
        <Field label="Incidents or Concerns" value={handover.incidents_or_concerns} testid="handover-ro-incidents" />
        <Field label="Tasks for Next Shift" value={handover.tasks_for_next_shift} testid="handover-ro-tasks" />
      </div>

      <div className="border-t pt-3 grid gap-3 sm:grid-cols-2" data-testid="handover-ro-meta">
        <Field label="Recorded" value={fmt(handover.created_at)} testid="handover-ro-recorded" />
        <Field label="Related Shift" value={handover.shift_id} testid="handover-ro-shift" mono />
      </div>

      <div className="border-t pt-3" data-testid="handover-ro-acks">
        <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Acknowledgements</h5>
        {acks.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="handover-ro-acks-empty">No acknowledgements recorded.</p>
        ) : (
          <ul className="space-y-1.5">
            {acks.map((a, i) => (
              <li key={a.id || i} className="text-sm" data-testid={`handover-ro-ack-${i}`}>
                <span className="font-medium text-foreground">{a.staff_name || 'Staff'}</span>
                {a.acknowledged_at ? <span className="text-muted-foreground"> · {fmt(a.acknowledged_at)}</span> : null}
                {a.comment ? <span className="text-muted-foreground"> — {a.comment}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default HandoverReadOnlyDetail;
